// src/LoginScreen.js
import React, { useState } from 'react';
import apiService from './apiService'; // Import your apiService
import { useAlert } from './contexts/AlertContext'; // To show alerts
import ForgotPassword from './ForgotPassword';
import RequestAccount from './RequestAccount';

const LoginScreen = ({ onLoginSuccess }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showRequestAccount, setShowRequestAccount] = useState(false);
    const { showAlert } = useAlert();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            // Call the login method from your apiService
            const response = await apiService.login(username, password);
            showAlert('Login successful!', 'success');
            onLoginSuccess(); // Callback to App.js to change view
        } catch (error) {
            console.error('Login error:', error);
            showAlert(error.message || 'Login failed. Please check your credentials.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-4 font-sans">
            <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md">
                <div className="text-center mb-6">
                    <img
                        src="https://placehold.co/60x60/007bff/ffffff?text=HMS"
                        alt="Hospital Logo"
                        className="h-16 w-16 mx-auto mb-4 object-contain rounded-full"
                    />
                    <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-2">Welcome Back</h2>
                    <p className="text-gray-600 dark:text-gray-400">Sign in to your Hospital Management account</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username</label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                            placeholder="Enter your username"
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out"
                            placeholder="Enter your password"
                            required
                            disabled={isLoading}
                        />
                    </div>
                    <button
                        type="submit"
                        className={`w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-150 ease-in-out flex items-center justify-center gap-2
                            ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        disabled={isLoading}
                    >
                        {isLoading && (
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                        {isLoading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
                
                {/* Forgot Password Link */}
                <div className="mt-4 text-center">
                    <button
                        onClick={() => setShowForgotPassword(true)}
                        className="text-blue-600 hover:underline text-sm"
                    >
                        Forgot your password?
                    </button>
                </div>

                <p className="mt-6 text-center text-gray-500 text-sm">
                    Don't have an account? <button onClick={() => setShowRequestAccount(true)} className="text-blue-600 hover:underline">Request Account</button>
                </p>
            </div>

            {/* Forgot Password Modal */}
            <ForgotPassword 
                isOpen={showForgotPassword} 
                onClose={() => setShowForgotPassword(false)} 
            />

            {/* Request Account Modal */}
            <RequestAccount 
                isOpen={showRequestAccount} 
                onClose={() => setShowRequestAccount(false)} 
            />
        </div>
    );
};

export default LoginScreen;
